import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, Sparkles, Compass, Target, Zap, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Reveal } from "@/hooks/useReveal";
import {
  ASSESSMENT_QUESTIONS,
  MAKECHANGE_PILLARS,
  tierFor,
  type PillarKey,
} from "@/content/makechange";

type Scores = Partial<Record<PillarKey, number>>;
const STORAGE_KEY = "mc_assessment_v1";

const Framework = () => {
  const [scores, setScores] = useState<Scores>({});
  const [idx, setIdx] = useState(0);
  const [started, setStarted] = useState(false);
  const [done, setDone] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (p.scores) setScores(p.scores);
        if (typeof p.idx === "number") setIdx(p.idx);
        if (p.done) setDone(true);
        if (p.started) setStarted(true);
      }
    } catch { /* noop */ }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ scores, idx, done, started }));
    } catch { /* noop */ }
  }, [scores, idx, done, started, hydrated]);

  const total = useMemo(
    () => Object.values(scores).reduce((a, b) => a + (b ?? 0), 0),
    [scores],
  );
  const tier = useMemo(() => tierFor(total), [total]);
  const answered = Object.keys(scores).length;
  const progress = (answered / ASSESSMENT_QUESTIONS.length) * 100;

  const startAssessment = () => {
    setStarted(true);
    setIdx(0);
    setDone(false);
    setScores({});
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    setTimeout(() => {
      document.getElementById("assessment")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const pickScore = (val: number) => {
    const q = ASSESSMENT_QUESTIONS[idx];
    setScores((s) => ({ ...s, [q.key]: val }));
    setTimeout(() => {
      if (idx < ASSESSMENT_QUESTIONS.length - 1) {
        setIdx(idx + 1);
      } else {
        setDone(true);
        setTimeout(() => {
          document.getElementById("results")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }, 220);
  };

  const sortedPillars = useMemo(() => {
    if (!done) return [];
    return ASSESSMENT_QUESTIONS.map((q) => ({
      key: q.key,
      letter: q.letter,
      pillar: q.pillar,
      score: scores[q.key] ?? 0,
    })).sort((a, b) => a.score - b.score);
  }, [done, scores]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="The MAKE CHANGE Framework & Assessment"
        description="Explore Dr. Sully's MAKE CHANGE leadership framework and take the free pillar assessment to identify your next growth lane."
        path="/framework"
      />
      <SiteNav />

      {/* HERO */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 bg-hero overflow-hidden">
        <div className="container max-w-4xl text-center">
          <Reveal variant="up">
            <span className="eyebrow !mx-auto">The Framework · Self-Assessment</span>
            <h1 className="font-display font-bold uppercase text-white text-4xl md:text-6xl lg:text-7xl mt-4 leading-[1.02]">
              The <span className="text-gradient-gold">M.A.K.E.C.H.A.N.G.E.</span><br />
              Framework
            </h1>
            <p className="text-silver text-base md:text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
              How Dr. Sully operates — a 10-pillar leadership model built to disrupt
              stagnation, lead with authority, and build systems that scale. Powered by
              AI consulting and Kingdom-focused strategy.
            </p>
            <p className="text-orange font-display uppercase tracking-[0.25em] text-xs mt-6">
              "If it doesn't make sense — make change."
            </p>
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS / OPERATING SYSTEM */}
      <section className="py-16 md:py-24">
        <div className="container max-w-5xl">
          <Reveal variant="up">
            <div className="text-center mb-12">
              <span className="eyebrow !mx-auto">How Dr. Sully Operates</span>
              <h2 className="font-display uppercase text-white text-3xl md:text-5xl mt-3">
                Ten letters. <span className="text-gradient-gold">One operating system.</span>
              </h2>
              <p className="text-silver text-base md:text-lg mt-5 max-w-2xl mx-auto">
                Each letter is a leadership competency. Together they form a diagnostic —
                so you know exactly where you're strong, where you're leaking, and what
                to build next.
              </p>
            </div>
          </Reveal>

          {(() => {
            const make = MAKECHANGE_PILLARS.slice(0, 4);
            const change = MAKECHANGE_PILLARS.slice(4);
            const Card = ({ p }: { p: (typeof MAKECHANGE_PILLARS)[number] }) => (
              <div className="relative bg-navy p-2.5 sm:p-4 md:p-5 h-full group overflow-hidden touch-manipulation transition-[background-color,box-shadow] duration-500 ease-out hover:bg-navy-elevated hover:shadow-[0_0_0_1px_hsl(var(--gold)/0.5),0_18px_40px_-18px_hsl(var(--gold)/0.45)] focus-within:bg-navy-elevated active:bg-navy-elevated">
                {/* cinematic gold sheen */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 group-active:opacity-100 group-focus-within:opacity-100 transition-opacity duration-500 bg-[radial-gradient(120%_80%_at_0%_0%,hsl(var(--gold)/0.18),transparent_55%)]"
                />
                {/* top accent line */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute top-0 left-0 h-px w-0 group-hover:w-full group-active:w-full group-focus-within:w-full transition-[width] duration-700 ease-out bg-gradient-to-r from-transparent via-gold to-transparent"
                />
                <div
                  className="relative font-display text-3xl sm:text-4xl md:text-5xl text-gradient-gold leading-none origin-center transform-gpu [will-change:transform,filter] motion-safe:transition-[transform,filter] motion-safe:duration-500 motion-safe:ease-out motion-safe:group-hover:scale-[1.03] motion-safe:group-active:scale-[1.03] motion-safe:group-focus-within:scale-[1.03] motion-safe:group-hover:drop-shadow-[0_0_14px_hsl(var(--gold)/0.45)] motion-safe:group-active:drop-shadow-[0_0_14px_hsl(var(--gold)/0.45)]"
                  style={{ paddingBottom: "0.15em" }}
                >
                  {p.letter}
                </div>
                <div className="relative text-[9px] sm:text-[10px] md:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] md:tracking-[0.25em] text-orange mt-2 md:mt-3 truncate">
                  {p.name}
                </div>
                <p className="relative hidden md:block text-silver text-sm mt-2 leading-relaxed transition-colors duration-500 group-hover:text-white/90">{p.desc}</p>
              </div>
            );
            return (
              <div className="space-y-px bg-border border border-border">
                {/* Row 1: MAKE — 4 cards, centered */}
                <div className="grid grid-cols-4 sm:max-w-[80%] sm:mx-auto gap-px">
                  {make.map((p, i) => (
                    <Reveal key={`${p.key}-${i}`} variant="up" delay={i * 40}>
                      <Card p={p} />
                    </Reveal>
                  ))}
                </div>
                {/* Row 2: CHANGE — 6 cards, always one row */}
                <div className="grid grid-cols-6 gap-px">
                  {change.map((p, i) => (
                    <Reveal key={`${p.key}-${i}`} variant="up" delay={(i + 4) * 40}>
                      <Card p={p} />
                    </Reveal>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Three principles */}
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {[
              { icon: Compass, title: "Diagnose first", desc: "Honest measurement before any prescription. No fluff, no flattery." },
              { icon: Target, title: "Build, don't plan", desc: "Every initiative tied to revenue, growth, or measurable impact." },
              { icon: Zap, title: "AI-amplified", desc: "Tools that serve the mission. Capacity for you, not dependency on me." },
            ].map((c, i) => (
              <Reveal key={c.title} variant="up" delay={i * 80}>
                <div className="premium-card p-6 h-full">
                  <c.icon className="h-6 w-6 text-gold" />
                  <h3 className="font-display uppercase text-white text-lg mt-4 tracking-wide">
                    {c.title}
                  </h3>
                  <p className="text-silver text-sm mt-2 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ASSESSMENT */}
      <section id="assessment" className="py-16 md:py-24 bg-navy-elevated/30 border-y border-border">
        <div className="container max-w-3xl">
          <Reveal variant="up">
            <div className="text-center mb-10">
              <span className="eyebrow !mx-auto">Free · 2 Minutes · No Email Required</span>
              <h2 className="font-display uppercase text-white text-3xl md:text-5xl mt-3 leading-tight">
                Take the <span className="text-gradient-gold">M.A.K.E.C.H.A.N.G.E.</span><br />
                Leadership Assessment
              </h2>
              <p className="text-silver text-base mt-5 max-w-xl mx-auto">
                10 questions. Rate yourself 1 (strongly disagree) to 10 (strongly agree).
                Be honest — this is for your growth, not your ego.
              </p>
            </div>
          </Reveal>

          {!started && !done && (
            <Reveal variant="scale">
              <div className="premium-card p-8 md:p-12 text-center">
                <Sparkles className="h-10 w-10 text-gold mx-auto" />
                <h3 className="font-display uppercase text-white text-2xl md:text-3xl mt-5 leading-tight">
                  Ready when you are.
                </h3>
                <p className="text-silver text-sm mt-4 max-w-md mx-auto">
                  You'll get a leadership level, your strongest pillars, and the areas
                  costing you the most — instantly. No download. No spam.
                </p>
                <Button
                  onClick={() => {
                    if (answered > 0) {
                      setStarted(true);
                      setTimeout(() => {
                        document.getElementById("assessment")?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }, 50);
                    } else {
                      startAssessment();
                    }
                  }}
                  className="mt-8 bg-cta hover:bg-cta hover:brightness-110 text-white font-display uppercase tracking-[0.2em] px-10 py-6 shadow-cta"
                >
                  {answered > 0 ? "Resume Assessment" : "Begin Assessment"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                {answered > 0 && (
                  <div className="mt-5 flex items-center justify-center gap-3 text-[10px] uppercase tracking-[0.25em] text-silver/70">
                    <span>{answered}/10 saved on this device</span>
                    <button
                      onClick={startAssessment}
                      className="inline-flex items-center gap-1 text-gold hover:text-orange transition-colors"
                    >
                      <RotateCcw className="h-3 w-3" /> Reset
                    </button>
                  </div>
                )}
              </div>
            </Reveal>
          )}

          {started && !done && (
            <div className="premium-card p-6 md:p-10">
              {/* Progress */}
              <div className="flex items-center justify-between text-xs uppercase tracking-[0.25em] text-silver">
                <span>
                  Question <span className="text-orange">{idx + 1}</span> of {ASSESSMENT_QUESTIONS.length}
                </span>
                <div className="flex items-center gap-3">
                  <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] text-silver/60">
                    <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
                    Auto-saved
                  </span>
                  <span className="text-gold">{Math.round(progress)}%</span>
                </div>
              </div>
              <div className="mt-3 h-1 bg-border overflow-hidden rounded-full">
                <div
                  className="h-full bg-gradient-to-r from-orange to-gold transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>

              {/* Question */}
              <div key={idx} className="mt-8 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="flex items-baseline gap-4">
                  <span className="font-display text-6xl md:text-7xl text-gradient-gold leading-none">
                    {ASSESSMENT_QUESTIONS[idx].letter}
                  </span>
                  <span className="text-xs uppercase tracking-[0.3em] text-orange">
                    {ASSESSMENT_QUESTIONS[idx].pillar}
                  </span>
                </div>
                <p className="text-white text-lg md:text-xl mt-5 leading-relaxed">
                  {ASSESSMENT_QUESTIONS[idx].prompt}
                </p>

                {/* Scale 1-10 */}
                <div className="mt-8">
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-silver mb-3">
                    <span>Strongly Disagree</span>
                    <span>Strongly Agree</span>
                  </div>
                  <div className="grid grid-cols-10 gap-1.5 sm:gap-2">
                    {Array.from({ length: 10 }).map((_, n) => {
                      const val = n + 1;
                      const current = scores[ASSESSMENT_QUESTIONS[idx].key];
                      const selected = current === val;
                      return (
                        <button
                          key={val}
                          type="button"
                          onClick={() => pickScore(val)}
                          aria-label={`Score ${val}`}
                          className={cn(
                            "aspect-square flex items-center justify-center font-display text-sm md:text-base border transition-all",
                            "hover:border-gold hover:bg-gold/10 hover:scale-105",
                            selected
                              ? "bg-gradient-to-br from-orange to-gold text-white border-transparent shadow-cta scale-105"
                              : "bg-navy border-border text-silver",
                          )}
                        >
                          {val}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* AI angle */}
                <div className="mt-8 border-l-2 border-gold/40 pl-4">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-gold">
                    AI Leadership Angle
                  </div>
                  <p className="text-silver text-sm mt-2 leading-relaxed">
                    {ASSESSMENT_QUESTIONS[idx].ai}
                  </p>
                </div>

                {/* Nav */}
                <div className="flex items-center justify-between mt-8">
                  <Button
                    variant="outline"
                    onClick={() => setIdx(Math.max(0, idx - 1))}
                    disabled={idx === 0}
                    className="border-gold/40 text-white hover:bg-gold/10 hover:text-white disabled:opacity-30"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back
                  </Button>
                  <span className="text-xs uppercase tracking-[0.25em] text-silver">
                    {answered}/10 answered
                  </span>
                </div>
              </div>
            </div>
          )}

          {done && (
            <div id="results" className="space-y-8">
              <Reveal variant="scale">
                <div className="premium-card p-8 md:p-12 text-center">
                  <div className="relative inline-flex">
                    <div className="absolute inset-0 bg-gold/30 blur-2xl rounded-full" />
                    <CheckCircle2 className="relative h-14 w-14 text-gold mx-auto" />
                  </div>
                  <div className="text-xs uppercase tracking-[0.3em] text-orange mt-6">
                    Your Leadership Level
                  </div>
                  <h2 className="font-display uppercase text-white text-3xl md:text-5xl mt-3 leading-tight">
                    {tier.level}
                  </h2>
                  <div className="mt-6 flex items-baseline justify-center gap-2">
                    <span className="font-display text-7xl md:text-8xl text-gradient-gold leading-none">
                      {total}
                    </span>
                    <span className="text-silver text-xl">/ 100</span>
                  </div>
                  <p className="text-silver text-base md:text-lg mt-6 max-w-xl mx-auto leading-relaxed">
                    {tier.summary}
                  </p>
                </div>
              </Reveal>

              {/* Per-pillar breakdown */}
              <Reveal variant="up">
                <div className="premium-card p-6 md:p-8">
                  <div className="text-xs uppercase tracking-[0.3em] text-gold">
                    Score breakdown
                  </div>
                  <h3 className="font-display uppercase text-white text-xl mt-2">
                    Your strongest — and where to focus
                  </h3>
                  <div className="mt-6 space-y-3">
                    {sortedPillars
                      .slice()
                      .reverse()
                      .map((p) => (
                        <div key={`${p.key}`} className="grid grid-cols-[auto,1fr,auto] items-center gap-4">
                          <div className="font-display text-2xl text-gold w-6 text-center">
                            {p.letter}
                          </div>
                          <div>
                            <div className="text-white text-sm uppercase tracking-wider">
                              {p.pillar}
                            </div>
                            <div className="mt-1.5 h-1.5 bg-border overflow-hidden rounded-full">
                              <div
                                className={cn(
                                  "h-full transition-all duration-700",
                                  p.score >= 8
                                    ? "bg-gradient-to-r from-gold to-orange"
                                    : p.score >= 5
                                      ? "bg-babyblue"
                                      : "bg-orange/60",
                                )}
                                style={{ width: `${p.score * 10}%` }}
                              />
                            </div>
                          </div>
                          <div className="font-display text-white tabular-nums w-10 text-right">
                            {p.score}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </Reveal>

              {/* Focus area */}
              <Reveal variant="up">
                <div className="border border-orange/40 bg-orange/[0.04] p-6 md:p-8">
                  <div className="text-xs uppercase tracking-[0.3em] text-orange">
                    Key insight
                  </div>
                  <p className="text-white/90 text-base md:text-lg mt-3 leading-relaxed">
                    Your lowest-scoring pillars are not weaknesses — they are your
                    greatest opportunities. Start with{" "}
                    <span className="text-gold font-display uppercase tracking-wider">
                      {sortedPillars[0]?.pillar}
                    </span>
                    {sortedPillars[1] && (
                      <>
                        {" "}and{" "}
                        <span className="text-gold font-display uppercase tracking-wider">
                          {sortedPillars[1].pillar}
                        </span>
                      </>
                    )}
                    .
                  </p>
                  <p className="text-silver text-sm mt-4 leading-relaxed">
                    AI is not replacing leaders. It is amplifying the ones willing to
                    learn, adapt, and lead with integrity.
                  </p>
                </div>
              </Reveal>

              {/* Next step */}
              <Reveal variant="up">
                <div className="premium-card p-8 md:p-10 text-center">
                  <div className="text-xs uppercase tracking-[0.3em] text-gold">
                    Your next move
                  </div>
                  <h3 className="font-display uppercase text-white text-2xl md:text-3xl mt-3 leading-tight">
                    {tier.next}
                  </h3>
                  <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to="/inquire">
                      <Button className="w-full sm:w-auto bg-cta hover:bg-cta hover:brightness-110 text-white font-display uppercase tracking-[0.2em] px-8 py-6 shadow-cta">
                        Book a Discovery Call <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      onClick={startAssessment}
                      className="w-full sm:w-auto border-gold/40 text-white hover:bg-gold/10 hover:text-white font-display uppercase tracking-[0.2em] px-8 py-6"
                    >
                      Retake Assessment
                    </Button>
                  </div>
                </div>
              </Reveal>
            </div>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Framework;
